"use client";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Icon } from "@/components/ui/icon";

function ProgressActionRoot({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex h-7 min-w-0 flex-1 items-center justify-end gap-1.5 overflow-hidden rounded-md border border-contrast/5 bg-contrast/3 px-1.5 sm:flex-initial sm:justify-start",
        className,
      )}
    >
      {children}
    </div>
  );
}

function ProgressActionStatusIcon({
  state,
}: {
  state: "processing" | "complete";
}) {
  if (state === "processing") {
    return (
      <div className="opacity-50">
        <Icon
          name="IconLoadingCircle"
          size={16}
          className="text-heading animate-spin"
        />
      </div>
    );
  }
  return (
    <div className="opacity-60">
      <Icon name="IconCheckCircle2" size={16} className="text-heading" />
    </div>
  );
}

function ProgressActionMain({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("flex items-center gap-2 min-w-0", className)}>
      {children}
    </div>
  );
}

function ProgressActionLabel({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        "text-sm font-medium text-heading leading-5 shrink-0",
        className,
      )}
    >
      {children}
    </span>
  );
}

function ProgressActionBarGroup({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("relative group shrink-0", className)}>{children}</div>
  );
}

function ProgressActionTrack({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "w-20 h-1.5 rounded-full bg-contrast/15 overflow-hidden flex",
        className,
      )}
    >
      {children}
    </div>
  );
}

function ProgressActionSegment({
  value,
  className,
}: {
  value: number;
  className: string;
}) {
  if (value <= 0) return null;
  return (
    <div
      className={cn("h-full transition-all duration-500 ease-out", className)}
      style={{ width: `${value}%` }}
    />
  );
}

function ProgressActionTooltip({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 rounded-md bg-surface-header border border-border text-xs whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity shadow-lg z-10",
        className,
      )}
    >
      {children}
    </div>
  );
}

function ProgressActionFraction({
  current,
  total,
  className,
}: {
  current: number;
  total: number;
  className?: string;
}) {
  return (
    <span className={cn("text-sm tabular-nums leading-5 shrink-0", className)}>
      <span className="text-contrast/85">{current} </span>
      <span className="text-contrast/60">/ {total}</span>
    </span>
  );
}

export const ProgressAction = Object.assign(ProgressActionRoot, {
  Root: ProgressActionRoot,
  StatusIcon: ProgressActionStatusIcon,
  Main: ProgressActionMain,
  Label: ProgressActionLabel,
  BarGroup: ProgressActionBarGroup,
  Track: ProgressActionTrack,
  Segment: ProgressActionSegment,
  Tooltip: ProgressActionTooltip,
  Fraction: ProgressActionFraction,
});
